import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../Contractor/ContractorDashboard.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import {
  FaClipboardList,
  FaFileAlt,
  FaTasks,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaUserCircle,
  FaBell,
  FaSearch,
  FaHome,
  FaCheckCircle,
  FaQrcode,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import MyTasks from './MyTasks';
import MyInspections from './MyInspections';
import UserRoleBadge from '../../shared/UserRoleBadge';
import MyDocuments from './MyDocuments';
import MySchedule from './MySchedule';
import ReportIssue from './ReportIssue';
import ProfileSettings from './ProfileSettings';
import ActivityLog from './ActivityLog';
import BuildingCheckIn from './BuildingCheckIn';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'staff') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint('/api/users/profile'), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && (data.firstName || data.email)) setUserName(data.firstName ? `${data.firstName}${data.lastName ? ' ' + data.lastName : ''}`.trim() : data.email);
        else setUserName('Staff User');
      })
      .catch(() => setUserName('Staff User'));
  }, [navigate]);

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleProfileClick = () => {
    setShowUserMenu(false);
    setShowProfile(true);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderOverviewSection();
      case 'checkin':
        return <BuildingCheckIn />;
      case 'inspections':
        return <MyInspections />;
      case 'documents':
        return <MyDocuments />;
      case 'schedule':
        return <MySchedule />;
      case 'tasks':
        return <MyTasks />;
      case 'report':
        return <ReportIssue />;
      case 'profile':
        return <ProfileSettings />;
      case 'activity':
        return <ActivityLog />;
      default:
        return renderOverviewSection();
    }
  };

  const [stats, setStats] = useState({
    pendingTasks: 0,
    completedToday: 0,
    overdueInspections: 0,
    upcomingInspections: 0
  });
  const [overviewLoading, setOverviewLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'staff') return;
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint('/api/staff/overview'), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : {})
      .then(data => {
        setStats({
          pendingTasks: data.pendingTasks ?? 0,
          completedToday: data.completedToday ?? 0,
          overdueInspections: data.overdueInspections ?? 0,
          upcomingInspections: data.upcomingInspections ?? 0
        });
      })
      .catch(() => {})
      .finally(() => setOverviewLoading(false));
  }, []);

  const recentActivity = [
    { date: new Date().toLocaleDateString(), action: 'Dashboard overview' },
    { date: '—', action: 'Complete tasks and inspections to see activity here' }
  ];

  function renderOverviewSection() {
    return (
      <div>
        <h2>Welcome, {userName}</h2>
        <div style={{ margin: '12px 0 20px 0' }}>
          <Link to="/dashboard/admin-panel" className="btn-primary">Open Onboarding Area</Link>
        </div>
        {overviewLoading ? (
          <p style={{ margin: '1rem 0' }}>Loading overview…</p>
        ) : (
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', margin: '2rem 0' }}>
          <div className="stat-card pending" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="stat-icon" style={{ fontSize: '2rem' }}>🕒</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>Pending Tasks</h3>
              <div className="stat-number" style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.pendingTasks}</div>
            </div>
          </div>
          <div className="stat-card completed" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="stat-icon" style={{ fontSize: '2rem' }}>✅</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>Completed Today</h3>
              <div className="stat-number" style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.completedToday}</div>
            </div>
          </div>
          <div className="stat-card overdue" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="stat-icon" style={{ fontSize: '2rem' }}>⚠️</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>Overdue Inspections</h3>
              <div className="stat-number" style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.overdueInspections}</div>
            </div>
          </div>
          <div className="stat-card scheduled" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="stat-icon" style={{ fontSize: '2rem' }}>📅</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>Upcoming Inspections</h3>
              <div className="stat-number" style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.upcomingInspections}</div>
            </div>
          </div>
        </div>
        )}
        <div className="overview-card" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', maxWidth: 600 }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Recent Activity</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {recentActivity.map((item, idx) => (
              <li key={idx} style={{ marginBottom: 12, borderBottom: '1px solid #ecf0f1', paddingBottom: 8 }}>
                <div style={{ fontWeight: 600, color: '#3498db' }}>{item.date}</div>
                <div>{item.action}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (showProfile) {
    return <ProfileSettings />;
  }

  return (
    <div className="contractor-dashboard">
      {/* Header */}
      <header className="contractor-header">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <img src={allSafeLogo} alt="CloudSafe Logo" className="header-logo" />
          <div className="header-title-group">
            <h1 className="header-title">Staff Dashboard</h1>
          </div>
        </div>
        <div className="header-right">
          <UserRoleBadge />
          <div className="header-user" onClick={() => setShowUserMenu(!showUserMenu)}>
            <FaUserCircle className="header-user-icon" />
            <span className="header-user-name">{userName}</span>
            {showUserMenu && (
              <div className="user-menu" style={{position:'absolute',top:'100%',right:0,background:'white',borderRadius:'8px',boxShadow:'0 4px 12px rgba(0,0,0,0.15)',minWidth:'150px',zIndex:1000,marginTop:'8px'}}>
                <button onClick={handleProfileClick} style={{display:'block',width:'100%',padding:'0.75rem 1rem',border:'none',background:'none',textAlign:'left',cursor:'pointer',fontSize:'0.9rem',color:'#334155'}}>Profile</button>
                <button onClick={handleLogout} style={{display:'block',width:'100%',padding:'0.75rem 1rem',border:'none',background:'none',textAlign:'left',cursor:'pointer',fontSize:'0.9rem',color:'#334155'}}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="contractor-layout">
        {/* Sidebar Navigation */}
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h3>Staff Menu</h3>
            <nav className="sidebar-nav">
              <button
                className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
                onClick={() => { setActiveSection('dashboard'); setSidebarOpen(false); }}
              >
                <FaHome />
                <span>Dashboard</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'checkin' ? 'active' : ''}`}
                onClick={() => { setActiveSection('checkin'); setSidebarOpen(false); }}
              >
                <FaQrcode />
                <span>Building Check-In</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'inspections' ? 'active' : ''}`}
                onClick={() => { setActiveSection('inspections'); setSidebarOpen(false); }}
              >
                <FaClipboardList />
                <span>My Inspections</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'documents' ? 'active' : ''}`}
                onClick={() => { setActiveSection('documents'); setSidebarOpen(false); }}
              >
                <FaFileAlt />
                <span>My Documents</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'schedule' ? 'active' : ''}`}
                onClick={() => { setActiveSection('schedule'); setSidebarOpen(false); }}
              >
                <FaCalendarAlt />
                <span>My Schedule</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'tasks' ? 'active' : ''}`}
                onClick={() => { setActiveSection('tasks'); setSidebarOpen(false); }}
              >
                <FaTasks />
                <span>My Tasks</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'report' ? 'active' : ''}`}
                onClick={() => { setActiveSection('report'); setSidebarOpen(false); }}
              >
                <FaExclamationTriangle />
                <span>Report an Issue</span>
              </button>
              <button
                className={`nav-item ${activeSection === 'activity' ? 'active' : ''}`}
                onClick={() => { setActiveSection('activity'); setSidebarOpen(false); }}
              >
                <FaCheckCircle />
                <span>Activity Log</span>
              </button>
            </nav>
          </div>
        </aside>
        <main className="contractor-content">
          <div className="dashboard-header">
            <div>
              <h1>Welcome, {userName} 🧑‍🔧</h1>
              <p className="dashboard-subtitle">
                {activeSection === 'dashboard' && 'Here\'s your staff dashboard overview'}
                {activeSection === 'checkin' && 'Scan QR codes or search IDs to check people into the building'}
                {activeSection === 'inspections' && 'View and complete your assigned inspections'}
                {activeSection === 'documents' && 'Access your certificates, logs, and uploads'}
                {activeSection === 'schedule' && 'Plan and view your upcoming tasks and inspections'}
                {activeSection === 'tasks' && 'Track your assigned tasks'}
                {activeSection === 'report' && 'Report a safety issue on-site'}
              </p>
            </div>
          </div>
          {renderActiveSection()}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <div className="cs-bottom-nav">
        <div className="cs-bottom-nav-items">
          {[
            { id: 'dashboard', icon: FaHome, label: 'Home' },
            { id: 'checkin', icon: FaQrcode, label: 'Check-In' },
            { id: 'inspections', icon: FaClipboardList, label: 'Inspections' },
            { id: 'tasks', icon: FaTasks, label: 'Tasks' },
            { id: 'report', icon: FaExclamationTriangle, label: 'Report' },
          ].map(item => (
            <button
              key={item.id}
              className={`cs-bottom-nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard; 